import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Loader2, Lock, Pencil, Trash2, Trophy } from 'lucide-react';
import StockModal from '@/components/StockModal';
import usePriceStream from '@/hooks/usePriceStream';
import LivePriceIndicator from '@/components/LivePriceIndicator';
import { fetchStockWithFallback } from '@/lib/stockFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Frequency code → display label. Massive's `frequency` field maps:
// 1 = annual, 2 = semi-annual, 4 = quarterly, 12 = monthly.
const FREQ_LABEL = {
  1: 'Annual',
  2: 'Semi-Annual',
  4: 'Quarterly',
  12: 'Monthly',
  52: 'Weekly',
};

function _formatDate(raw) {
  if (!raw) return '—';
  const s = String(raw).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function _formatPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(2)}%`;
}

function _formatUSD(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `$${Number(v).toFixed(2)}`;
}

/**
 * StockDataBar — Round 84k Phase 5.9 (Feb 15, 2026).
 *
 * Compact bar showing the columns the user explicitly requested:
 * price · yield % · div/yr · ex-div · pay date · payout frequency.
 * Skips news icon, day-change %, and time machine (per user spec).
 *
 * Clicking the ticker fires `onOpen(ticker)` to launch the Stock Modal
 * in-place on this tab.
 */
function StockDataBar({ ticker, stock, livePrice, onOpen }) {
  const price = livePrice ?? stock?.price;
  const yieldPct = stock?.dividend_yield;
  const annualDiv = stock?.annual_dividend ?? stock?.dividend_rate;
  const exDate = stock?.ex_dividend_date;
  const payDate = stock?.next_payment_date ?? stock?.last_payment_date;
  const freq = stock?.frequency != null ? FREQ_LABEL[stock.frequency] : (stock?.payout_frequency || stock?.dividend_frequency);

  if (!stock) {
    return (
      <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground flex items-center gap-2" data-testid={`community-bar-loading-${ticker}`}>
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading {ticker}…
      </div>
    );
  }

  return (
    <div
      className="px-3 py-2 border-t border-border grid grid-cols-2 sm:grid-cols-7 gap-2 items-center text-xs"
      data-testid={`community-bar-${ticker}`}
    >
      <button
        type="button"
        onClick={() => onOpen(ticker)}
        className="font-bold text-violet-700 dark:text-violet-300 hover:underline text-left col-span-2 sm:col-span-1"
        data-testid={`community-bar-ticker-${ticker}`}
        title={`Open ${ticker} stock modal`}
      >
        {ticker}
      </button>
      <div className="text-right sm:text-left">
        <span className="text-[10px] text-muted-foreground sm:hidden">Price </span>
        <span className="font-mono font-semibold">{_formatUSD(price)}</span>
      </div>
      <div className="text-right sm:text-left">
        <span className="text-[10px] text-muted-foreground sm:hidden">Yield </span>
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">{_formatPct(yieldPct)}</span>
      </div>
      <div className="text-right sm:text-left">
        <span className="text-[10px] text-muted-foreground sm:hidden">Div/yr </span>
        <span className="font-mono">{_formatUSD(annualDiv)}</span>
      </div>
      <div className="text-right sm:text-left">
        <span className="text-[10px] text-muted-foreground sm:hidden">Ex-Div </span>
        <span>{_formatDate(exDate)}</span>
      </div>
      <div className="text-right sm:text-left">
        <span className="text-[10px] text-muted-foreground sm:hidden">Pay </span>
        <span className="text-cyan-700 dark:text-cyan-400">{_formatDate(payDate)}</span>
      </div>
      <div className="text-right sm:text-left col-span-2 sm:col-span-1">
        <span className="text-[10px] text-muted-foreground sm:hidden">Payout </span>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300">
          {freq || '—'}
        </span>
      </div>
    </div>
  );
}

/**
 * CommunityWatchlistRowDetail — fetches stock data for the tickers in
 * a single expanded watchlist, subscribes to live prices for those
 * tickers (so the bars flicker LIVE once Phase C activates), and
 * renders one StockDataBar per ticker.
 */
export function CommunityWatchlistRowDetail({ tickers, onOpen }) {
  const [stocks, setStocks] = useState({});  // {TICKER: stock_doc}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      // Fire all fetches in parallel; tolerate individual failures.
      const results = await Promise.allSettled(
        tickers.map((t) => fetchStockWithFallback(t, { timeoutMs: 3500 }))
      );
      if (cancelled) return;
      const map = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) {
          map[String(tickers[i]).toUpperCase()] = r.value;
        }
      });
      setStocks(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [tickers.join(',')]);

  const subTickers = useMemo(
    () => tickers.map(t => String(t || '').toUpperCase()).filter(Boolean),
    [tickers.join(',')]
  );
  const { prices, status } = usePriceStream(subTickers);

  return (
    <div data-testid="community-watchlist-row-detail">
      <div className="px-3 py-1 border-t border-border bg-muted/20 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
          {tickers.length} {tickers.length === 1 ? 'ticker' : 'tickers'}
        </span>
        <LivePriceIndicator status={status} />
      </div>
      {loading && (
        <div className="px-3 py-3 text-center text-xs text-muted-foreground border-t border-border">
          <Loader2 className="w-3 h-3 animate-spin inline mr-2" />
          Loading bar data…
        </div>
      )}
      {!loading && tickers.map((t) => {
        const TK = String(t).toUpperCase();
        return (
          <StockDataBar
            key={TK}
            ticker={TK}
            stock={stocks[TK]}
            livePrice={prices[TK]?.price}
            onOpen={onOpen}
          />
        );
      })}
    </div>
  );
}

/**
 * CommunityWatchlistRow — wraps the existing watchlist header (name +
 * race-ready badge + tickers chips + Edit / Delete) and toggles the
 * expanded bar list under it. Edit/Delete are HIDDEN when the list is
 * locked in an active leaderboard race (per user spec).
 */
export function CommunityWatchlistRow({ wl, onEdit, onDelete, onOpenStock }) {
  const [expanded, setExpanded] = useState(false);
  const tCount = (wl.tickers || []).length;
  const raceReady = tCount === 5;
  const needed = Math.max(0, 5 - tCount);
  const locked = !!wl.leaderboard_locked;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`community-watchlist-row-${wl.watchlist_id}`}>
      <div className="p-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition"
          aria-expanded={expanded}
          data-testid={`community-watchlist-expand-${wl.watchlist_id}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground truncate flex items-center gap-2 flex-wrap">
            <span className="truncate">{wl.name}</span>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                raceReady
                  ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                  : 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
              }`}
              title={raceReady
                ? 'Eligible for the Total Return Race in the Leaderboard tab.'
                : `Needs ${needed} more ticker${needed === 1 ? '' : 's'} to enter the Total Return Race.`}
              data-testid={`community-watchlist-race-badge-${wl.watchlist_id}`}
            >
              <Trophy className="w-2.5 h-2.5" />
              {raceReady ? 'Race-Ready 5/5' : `${tCount}/5 · Add ${needed}`}
            </span>
            {locked && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300"
                title="This list is locked in an active Total Return Race. Editing and deletion re-open after results freeze Friday at 4:00 PM ET."
                data-testid={`community-watchlist-locked-${wl.watchlist_id}`}
              >
                <Lock className="w-2.5 h-2.5" />
                Locked
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {(wl.tickers || []).map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300">{t}</span>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">{wl.follower_count || 0} followers · updated {new Date(wl.updated_at).toLocaleDateString()}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          {locked ? (
            <span className="text-[10px] text-amber-700 dark:text-amber-300 italic px-2 self-center">Race active</span>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onEdit(wl)}
                className="px-2 py-1 rounded border border-border text-xs hover:bg-muted transition"
                data-testid={`community-watchlist-edit-${wl.watchlist_id}`}
              >
                <Pencil className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => onDelete(wl)}
                className="px-2 py-1 rounded border border-border text-xs hover:bg-muted transition"
                data-testid={`community-watchlist-delete-${wl.watchlist_id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      </div>
      {expanded && (wl.tickers || []).length > 0 && (
        <CommunityWatchlistRowDetail tickers={wl.tickers} onOpen={onOpenStock} />
      )}
    </div>
  );
}

/**
 * Wraps the row + the in-place Stock Modal so clicking a ticker in
 * a bar opens the modal without navigating away from the tab. Used by
 * CommunityWatchlistsTab as the per-row renderer.
 */
export function CommunityWatchlistRowWithModal({ wl, onEdit, onDelete }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStock, setModalStock] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const handleOpenStock = async (ticker) => {
    setModalOpen(true);
    setModalLoading(true);
    setModalStock(null);
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setModalStock(fresh),
      });
      setModalStock(data);
    } catch {
      setModalStock(null);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <CommunityWatchlistRow
        wl={wl}
        onEdit={onEdit}
        onDelete={onDelete}
        onOpenStock={handleOpenStock}
      />
      <StockModal
        stock={modalStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setModalStock(null); }}
        loading={modalLoading}
      />
    </>
  );
}
